import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Alert, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { ErrorState } from '@/components/common/ErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PageHeader } from '@/components/common/PageHeader';
import { MFA_MANDATORY_ROLES } from '@/constants/roles';
import { EnableMfaDialog } from '@/features/identity/components/EnableMfaDialog';
import { ResetMfaDialog } from '@/features/identity/components/ResetMfaDialog';
import { useProfile } from '@/features/identity/hooks/useProfile';
import { useToast } from '@/hooks/useToast';

/**
 * `/account/mfa-setup` — the account's own MFA controls: enable, re-bind to a new authenticator, and
 * (where the role permits) turn MFA off.
 *
 * <p>Both removal paths go through {@link ResetMfaDialog}, which re-authenticates on the account's own
 * credential — its password, or a fresh provider sign-in for an account that has no password — plus an
 * emailed code, rather than a TOTP code. A user whose device is lost cannot produce a TOTP code, and that
 * lockout is the entire reason this page offers a removal path at all.</p>
 *
 * <p>MFA is mandatory for the Admin and Recruiter roles (BR-2), so those accounts get "Set up a new
 * device" but no off switch, and the page says why rather than leaving a missing button
 * unexplained. The server refuses a turn-off from them regardless.</p>
 */
export function MfaSetupPage() {
  const { user, isLoading, error, refetch } = useProfile();
  const [enableOpen, setEnableOpen] = useState(false);
  const [resetIntent, setResetIntent] = useState<'rebind' | 'disable' | null>(null);
  const toast = useToast();

  const mfaMandatory = user?.roles.some((role) => MFA_MANDATORY_ROLES.includes(role)) ?? false;

  const onReset = (intent: 'rebind' | 'disable') => {
    void refetch();
    if (intent === 'rebind') {
      // Straight into enrollment: the account is single-factor until it re-binds, and a
      // mandatory-MFA role cannot sign in again without doing so.
      setEnableOpen(true);
      toast.showInfo('Authenticator removed. Scan the new QR code to finish.');
    } else {
      toast.showSuccess('Multi-factor authentication is off.');
    }
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Multi-factor authentication"
        description="Add an extra layer of security to your account."
      />
      {isLoading ? <LoadingSpinner label="Loading security settings…" /> : null}
      {!isLoading && error ? <ErrorState message={error} onRetry={refetch} /> : null}
      {!isLoading && !error && user ? (
        <Card sx={{ maxWidth: 560 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ShieldOutlinedIcon color={user.mfaEnabled ? 'success' : 'disabled'} />
                <Typography variant="body1">
                  MFA is currently {user.mfaEnabled ? 'enabled' : 'not enabled'} on your account.
                </Typography>
              </Stack>

              {!user.mfaEnabled ? (
                <>
                  {mfaMandatory ? (
                    <Alert severity="warning">
                      Your role requires multi-factor authentication. You&apos;ll be asked to set it
                      up the next time you sign in.
                    </Alert>
                  ) : null}
                  <Button
                    variant="contained"
                    onClick={() => setEnableOpen(true)}
                    sx={{ alignSelf: 'flex-start', minHeight: 44 }}
                  >
                    Enable MFA
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Lost or replaced your phone? Set up a new device. You&apos;ll confirm{' '}
                    {user.hasPassword ? 'your password' : 'your identity with your sign-in provider'}{' '}
                    and a code we email you, so your old authenticator isn&apos;t needed. This signs
                    you out everywhere.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="contained"
                      onClick={() => setResetIntent('rebind')}
                      sx={{ minHeight: 44 }}
                    >
                      Set up a new device
                    </Button>
                    {!mfaMandatory ? (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setResetIntent('disable')}
                        sx={{ minHeight: 44 }}
                      >
                        Turn off MFA
                      </Button>
                    ) : null}
                  </Stack>
                  {mfaMandatory ? (
                    <Alert severity="info">
                      Multi-factor authentication is required for your role and cannot be turned off.
                    </Alert>
                  ) : null}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <EnableMfaDialog open={enableOpen} onClose={() => setEnableOpen(false)} onEnabled={refetch} />
      {/* The credential shape comes from the freshly-fetched /account/me above, not the login-time
          session snapshot, so a provider link added mid-session is reflected. Rendered only with a
          loaded user for the same reason: defaulting hasPassword either way would guess. */}
      {user ? (
        <ResetMfaDialog
          open={resetIntent !== null}
          intent={resetIntent ?? 'rebind'}
          canTurnOffCompletely={!mfaMandatory}
          hasPassword={user.hasPassword}
          linkedProviders={user.linkedOAuthProviders}
          onClose={() => setResetIntent(null)}
          onReset={onReset}
        />
      ) : null}
    </Stack>
  );
}
